/*
  GovernanceAuditLogWidget — Read-only history of governance changes
  (spec §10 traceability). Reads /api/governance/audit-log.

  Rows include config edits (thresholds, Class D toggle, retention),
  pathway-override PUT/DELETE, and retention sweeps. Each row shows
  actor, timestamp, target, and a compact JSON before/after diff.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollText, RefreshCcw } from "lucide-react";


function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function short(obj) {
  if (obj === null || obj === undefined) return "—";
  try {
    const s = JSON.stringify(obj);
    return s.length > 120 ? s.slice(0, 117) + "…" : s;
  } catch { return String(obj); }
}


export default function GovernanceAuditLogWidget() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [targetFilter, setTargetFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (targetFilter) params.target = targetFilter;
      const r = await api.get("/governance/audit-log", { params });
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error("Audit log unavailable", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }, [targetFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-4" data-testid="governance-audit-log-widget">
      <div className="flex items-center gap-2 border rounded-md px-3 py-2 mb-3 border-zinc-200 bg-zinc-50 text-zinc-900">
        <ScrollText className="h-4 w-4 shrink-0" />
        <div className="font-semibold text-sm">Governance audit log</div>
        <div className="ml-auto flex items-center gap-2">
          <select
            value={targetFilter}
            onChange={(e) => setTargetFilter(e.target.value)}
            className="text-xs bg-white border border-zinc-300 rounded px-1 py-0.5"
            data-testid="audit-log-target-filter"
          >
            <option value="">All targets</option>
            <option value="config">Config</option>
            <option value="pathway_override">Pathway overrides</option>
            <option value="retention">Retention sweep</option>
          </select>
          <Button
            size="sm" variant="ghost"
            className="h-6 px-2 hover:bg-zinc-200"
            onClick={load}
            data-testid="audit-log-refresh"
          >
            <RefreshCcw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {!loading && rows.length === 0 && (
        <div className="text-sm text-zinc-600 italic p-4 text-center">
          No changes recorded yet. Governance edits will land here.
        </div>
      )}

      {rows.length > 0 && (
        <div className="border border-zinc-200 rounded overflow-hidden">
          <div className="max-h-[380px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 text-zinc-600 sticky top-0">
                <tr className="text-left">
                  <th className="px-2 py-1 font-semibold">When</th>
                  <th className="px-2 py-1 font-semibold">Actor</th>
                  <th className="px-2 py-1 font-semibold">Target</th>
                  <th className="px-2 py-1 font-semibold">Action</th>
                  <th className="px-2 py-1 font-semibold">Change</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                    <td className="px-2 py-1 whitespace-nowrap text-zinc-600 font-mono-num">
                      {fmtTime(r.at)}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">{r.actor || "—"}</td>
                    <td className="px-2 py-1">
                      <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-zinc-300 bg-white">
                        {r.target}
                      </span>
                      {r.context?.module_key && (
                        <span className="ml-1 text-2xs text-zinc-500 font-mono-num">
                          {r.context.module_key}/{r.context.field_key}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1 capitalize">{r.action}</td>
                    <td className="px-2 py-1 font-mono-num text-2xs text-zinc-700">
                      {r.action === "sweep" ? (
                        <>
                          audio:{r.context?.audio_deleted_count ?? 0}{" · "}
                          transcript:{r.context?.transcript_deleted_count ?? 0}
                        </>
                      ) : (
                        <>
                          {short(r.before)} → {short(r.after)}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}
